import { useEffect, useMemo, useState } from 'react';

// Real frames for picks that have no poster of their own. The server makes ONE poster per
// sentence, so the pieces of a split clip all showed their parent's first frame in the export
// window. This seeks a hidden <video> to the piece's start and draws that frame onto a canvas
// (the timeline filmstrip's trick). If the video host blocks canvas reads (CORS) or the video
// won't load, it quietly gives up and the poster stays.

const WIDTH = 640;            // big enough for the preview frame, small enough to be quick
const LOAD_TIMEOUT_MS = 15000;
const SEEK_TIMEOUT_MS = 8000;

const frames = new Map();     // "url|t" → Promise<dataURL | null>
const broken = new Set();     // urls that can't be read
const players = new Map();    // url → { ready: Promise<video|null>, chain: Promise }

const withTimeout = (promise, ms) => new Promise((resolve) => {
  const timer = setTimeout(() => resolve(null), ms);
  promise.then((v) => { clearTimeout(timer); resolve(v); }, () => { clearTimeout(timer); resolve(null); });
});

const playerFor = (url) => {
  if (players.has(url)) return players.get(url);
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  const ready = withTimeout(new Promise((resolve, reject) => {
    video.addEventListener('loadeddata', () => resolve(video), { once: true });
    video.addEventListener('error', () => reject(new Error('video')), { once: true });
  }), LOAD_TIMEOUT_MS).then((v) => {
    if (!v) broken.add(url);
    return v;
  });
  video.src = url;
  const player = { ready, chain: Promise.resolve() };
  players.set(url, player);
  return player;
};

const draw = (video, t) => withTimeout(new Promise((resolve, reject) => {
  const onSeeked = () => {
    try {
      const w = Math.min(WIDTH, video.videoWidth || WIDTH);
      const h = Math.round((w * (video.videoHeight || 9)) / (video.videoWidth || 16)) || Math.round((w * 9) / 16);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(video, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    } catch (e) {
      reject(e); // tainted canvas: this host doesn't allow it
    }
  };
  video.addEventListener('seeked', onSeeked, { once: true });
  // a hair inside the clip, so the frame is the piece's own and not the one before it
  video.currentTime = Math.max(0, t + 0.04);
}), SEEK_TIMEOUT_MS);

/** The frame at `t` seconds of `url` as a data URL, or null if it can't be read. */
export const grabFrame = (url, t) => {
  if (!url || !Number.isFinite(t) || typeof document === 'undefined' || broken.has(url)) return Promise.resolve(null);
  const key = `${url}|${t.toFixed(2)}`;
  if (frames.has(key)) return frames.get(key);
  const player = playerFor(url);
  // one seek at a time per video
  const result = player.chain.then(() => player.ready).then((video) => {
    if (!video || broken.has(url)) return null;
    return draw(video, t).then((src) => {
      if (!src) broken.add(url);
      return src;
    });
  });
  player.chain = result.catch(() => null);
  const safe = result.catch(() => {
    broken.add(url);
    return null;
  });
  frames.set(key, safe);
  return safe;
};

/** {id: dataURL} for each wanted {id, url, t} once its frame is ready (a frame for an older
 *  start of the same id — the clip was split again elsewhere — is never shown). */
export const useClipFrames = (wanted) => {
  const [got, setGot] = useState({});
  const list = wanted || [];
  const keyOf = (w) => `${w.url}|${Number(w.t).toFixed(2)}`;
  const sig = list.map((w) => `${w.id}|${keyOf(w)}`).join(',');
  useEffect(() => {
    let alive = true;
    list.forEach((w) => {
      const key = keyOf(w);
      grabFrame(w.url, w.t).then((src) => {
        if (alive && src) setGot((prev) => (prev[key] === src ? prev : { ...prev, [key]: src }));
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return useMemo(() => {
    const out = {};
    list.forEach((w) => {
      const src = got[keyOf(w)];
      if (src) out[w.id] = src;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [got, sig]);
};
